/**
 * PHARMAKON — the paid user seat as the owner and the user read it (X XWP-69, CP §15.3, XO-16).
 *
 * Pure: the sentences of the create form's quote, the payment dialog and the « Mon siège » line, from
 * the server's answers (GET /users/sieges/devis, the POST /users payment envelope, GET /users/moi/siege).
 * Every amount is the server's decimal string — never computed nor typed here (XMD-CP-19: no seat price
 * literal in the SPA). Customer wording « Utilisateur », never « Caissier » (the role label is kept).
 */
import { formatDecimal } from "./number-format.js";

/** « 4 512 DA » — whole DA when the cents are zero (the seat charges are whole DA). */
export function montantDa(v) {
  if (v == null || v === "") return "—";
  const s = String(v);
  const entier = /\.00$/.test(s) || !s.includes(".");
  return `${formatDecimal(s, { decimals: entier ? 0 : 2 })} DA`;
}

/** « 07/10/2027 » in the officine's reading (dates come as ISO strings). */
export function dateCourte(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("fr-FR");
}

export const MESSAGES_SIEGE = Object.freeze({
  TITRE: "Siège utilisateur",
  CHARGEMENT: "Calcul du siège…",
  ERREUR: "Le montant du siège n'a pas pu être calculé. Réessayez dans un instant.",
  SANS_SIEGE: "Ce rôle n'occupe pas de siège payant.",
  DIALOG_TITRE: "Paiement du siège utilisateur",
  INACTIF: "a été créé mais reste inactif jusqu'au paiement de son siège",
  APRES_PAIEMENT:
    "Le compte sera activé automatiquement dès la confirmation du paiement. Vous pouvez rouvrir ce lien à tout moment.",
  RENVOYER: "Renvoyer le lien de paiement",
  FERMER: "Fermer",
  ACTIVE: "Utilisateur activé.",
  RETOUR_PAYE: "Paiement du siège confirmé — l'utilisateur est actif.",
  RETOUR_EN_ATTENTE: "Paiement en cours de confirmation. L'utilisateur sera activé dès sa réception.",
  RETOUR_ECHEC: "Le paiement du siège n'a pas abouti. Vous pouvez renvoyer le lien depuis la liste de l'équipe.",
  A_REGLER: "Siège à régler",
});

/**
 * The quote of the create form.
 * @returns {{ ton: "info"|"success"|"accent", montant: string|null, barre: string|null, lignes: string[] }|null}
 */
export function presentationDevis(devis) {
  if (!devis || !devis.consomme_siege) return null;
  const fin = dateCourte(devis.periode_fin);
  if (devis.origine === "COLLABORATEUR") {
    return {
      ton: "success",
      montant: "0 DA",
      barre: devis.tarif_annuel_da ? montantDa(devis.tarif_annuel_da) : null,
      lignes: ["Offert — Pharmacie Collaboratrice. L'utilisateur est activé immédiatement."],
    };
  }
  if (devis.origine === "ESSAI") {
    return {
      ton: "info",
      montant: "0 DA",
      barre: null,
      lignes: [
        "Aucun paiement pendant la période d'essai : l'utilisateur est activé immédiatement.",
        "Son siège sera facturé avec la première licence de l'officine.",
      ],
    };
  }
  return {
    ton: "accent",
    montant: montantDa(devis.montant_da),
    barre: null,
    lignes: [
      `À payer maintenant : ${devis.jours} jour${devis.jours > 1 ? "s" : ""} restant${devis.jours > 1 ? "s" : ""} de la licence${fin ? `, jusqu'au ${fin}` : ""}.`,
      `Ensuite ${montantDa(devis.tarif_annuel_da)} par an, facturés avec chaque renouvellement de la licence.`,
      "L'utilisateur est activé dès la confirmation du paiement.",
    ],
  };
}

/**
 * The payment dialog after a creation or a re-issue (the server's envelope + who it is for).
 * @returns {{ peutPayer: boolean, montant: string, nom: string, erreur: string|null, recurrence: string|null }}
 */
export function presentationPaiement({ user, payment } = {}) {
  const s = payment?.siege ?? null;
  const fin = dateCourte(s?.periode_fin);
  return {
    peutPayer: Boolean(payment?.checkout_url),
    montant: montantDa(s?.montant_da),
    nom: `${user?.prenom ?? ""} ${user?.nom ?? ""}`.trim(),
    erreur: payment?.error ?? null,
    recurrence: s?.tarif_annuel_da
      ? `Période couverte jusqu'au ${fin ?? "—"}, puis ${montantDa(s.tarif_annuel_da)} par an avec le renouvellement de la licence.`
      : null,
  };
}

const ETATS = Object.freeze({
  ACTIF: { ton: "success", libelle: "Siège actif" },
  EN_ATTENTE_PAIEMENT: { ton: "warning", libelle: "En attente de paiement" },
  ECHU: { ton: "neutral", libelle: "Siège échu" },
  AUCUN: { ton: "neutral", libelle: "Aucun siège" },
  SANS_SIEGE: { ton: "neutral", libelle: "Sans siège payant" },
});

/**
 * The read-only « Mon siège » line of /moncompte (one's own only).
 * @returns {{ ton: string, libelle: string, detail: string|null }}
 */
export function presentationMonSiege(m) {
  if (!m) return { ton: "neutral", libelle: "—", detail: null };
  const e = ETATS[m.etat] ?? ETATS.AUCUN;
  if (!m.consomme_siege) return { ...e, detail: m.message ?? null };
  const s = m.siege;
  const fin = dateCourte(s?.periode_fin);
  let detail = m.message ?? null;
  if (m.etat === "ACTIF") {
    detail = [s?.origine_libelle, fin ? `jusqu'au ${fin}` : "jusqu'au prochain renouvellement de la licence"]
      .filter(Boolean)
      .join(" · ");
  } else if (m.etat === "EN_ATTENTE_PAIEMENT") {
    detail = `${montantDa(s?.montant_da)} à régler par le propriétaire de l'officine.`;
  }
  return { ...e, detail, recurrence: m.recurrence ?? null };
}

/** The checkout id the provider appends to the return URL (`?checkout_id=`), or null. */
export function checkoutIdDeRetour(search) {
  const id = new URLSearchParams(search ?? "").get("checkout_id");
  return id && /^[A-Za-z0-9_-]{1,100}$/.test(id) ? id : null;
}

/** « 18/10/2026 à 07:42 » (dates come as ISO strings). */
export function dateHeure(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.toLocaleDateString("fr-FR")} à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
}

/** Two decimal strings name the same amount (« 7000 » = « 7000.00 ») — compared as text, never as floats. */
export function memeMontant(a, b) {
  const norme = (v) => {
    if (v == null || v === "") return null;
    const [ent, dec = ""] = String(v).trim().split(".");
    const e = ent.replace(/^0+(?=\d)/, "");
    const d = dec.replace(/0+$/, "");
    return d ? `${e}.${d}` : e;
  };
  const na = norme(a);
  return na != null && na === norme(b);
}

export const MESSAGES_TARIF = Object.freeze({
  TITRE: "Siège utilisateur",
  SOUS_TITRE: "Prix annuel d'un utilisateur de l'officine (hors propriétaire), fixé par l'administration PHARMAKON",
  EN_VIGUEUR: "Prix en vigueur",
  CHAMP: "Nouveau prix annuel",
  ENREGISTRER: "Enregistrer le prix",
  ENREGISTRE: (m) => `Nouveau prix en vigueur : ${m} par utilisateur et par an.`,
  INCHANGE: "Ce prix est déjà en vigueur : rien n'a été modifié.",
  NON_CONFIGURE: "Aucun prix en vigueur : aucun siège ne peut être facturé.",
  REGLE:
    "Le nouveau prix s'applique dès son enregistrement aux utilisateurs ajoutés et aux renouvellements de licence à venir. Un paiement déjà ouvert garde son montant.",
  HISTORIQUE: "Historique des prix",
  HISTORIQUE_VIDE: "Aucune version enregistrée.",
  HISTORIQUE_ERREUR: "L'historique n'a pas pu être chargé.",
  ERREUR: "Le prix n'a pas pu être enregistré.",
  bornes: (min, max) => `Entre ${min} et ${max}. Entrée pour enregistrer.`,
});

/**
 * The admin cockpit's reading of a platform price (GET /admin/billing/pricing → `tarifs[cle]`).
 * @returns {{ montant: string, depuis: string|null, auteur: string|null, min: string|null, max: string|null, unite: string|null, configure: boolean }}
 */
export function presentationTarif(t) {
  const v = t?.en_vigueur ?? null;
  const auteur = v?.modifie_par ? `${v.modifie_par.prenom ?? ""} ${v.modifie_par.nom ?? ""}`.trim() || null : null;
  return {
    configure: Boolean(v),
    montant: montantDa(v?.montant_da),
    depuis: dateHeure(v?.effectif_le),
    auteur,
    min: t?.montant_min ?? null,
    max: t?.montant_max ?? null,
    unite: t?.unite ?? null,
  };
}
